import type { Event } from '../types/api';
import { getVibes } from './eventPresentation';
import { parseEventLocalDateTime } from './eventDateTime';

/**
 * Marker styling for event maps: colour by vibe, opacity by how far out the
 * event is. Both are derived so the legend, the pins, and the side list can
 * never disagree.
 */

export interface VibeStyle {
  key: string;
  label: string;
  color: string;
}

/**
 * Vibe families, in priority order. An event is styled by the first family
 * that matches any of its vibes, so a "Keynote + Networking" event reads as a
 * conference rather than flipping between colours on re-render.
 */
export const VIBE_STYLES: VibeStyle[] = [
  { key: 'conference', label: 'Conference / keynote', color: '#1890ff' },
  { key: 'learning', label: 'Training / workshop', color: '#52c41a' },
  { key: 'networking', label: 'Networking', color: '#fa8c16' },
  { key: 'social', label: 'Social', color: '#eb2f96' },
  { key: 'competition', label: 'Hackathon / competition', color: '#722ed1' },
  { key: 'executive', label: 'Executive', color: '#13c2c2' },
];

const VIBE_MEMBERS: Record<string, string[]> = {
  conference: ['conference', 'keynote', 'expo', 'panel'],
  learning: ['classroom', 'seminar', 'hands-on', 'demo'],
  networking: ['networking', 'roundtable', 'community'],
  social: ['social', 'party'],
  competition: ['hackathon', 'competition'],
  executive: ['executive'],
};

export const DEFAULT_VIBE_STYLE: VibeStyle = { key: 'other', label: 'Other', color: '#8c8c8c' };

/** The colour family for an event, falling back to a neutral grey. */
export function getVibeStyle(event: Partial<Event> | null | undefined): VibeStyle {
  const vibes = getVibes(event).map((v) => v.toLowerCase());
  if (vibes.length === 0) return DEFAULT_VIBE_STYLE;

  for (const style of VIBE_STYLES) {
    const members = VIBE_MEMBERS[style.key] || [];
    if (vibes.some((vibe) => members.includes(vibe))) return style;
  }
  return DEFAULT_VIBE_STYLE;
}

export type Proximity = 'soon' | 'month' | 'later';

export const PROXIMITY_OPACITY: Record<Proximity, number> = {
  soon: 1,
  month: 0.72,
  later: 0.45,
};

export const PROXIMITY_LABELS: Record<Proximity, string> = {
  soon: 'Within a week',
  month: 'Within a month',
  later: 'More than a month out',
};

/** Whole days from today to the event's start date, or null if unparseable. */
export function daysUntil(event: Partial<Event> | null | undefined, reference: Date = new Date()): number | null {
  const parsed = parseEventLocalDateTime(event?.startDate);
  if (!parsed) return null;
  const start = new Date(parsed.year, parsed.month - 1, parsed.day);
  const today = new Date(reference.getFullYear(), reference.getMonth(), reference.getDate());
  return Math.round((start.getTime() - today.getTime()) / 86_400_000);
}

/** Nearer events render solid; further ones fade back so soon-ish events pop. */
export function getProximity(event: Partial<Event> | null | undefined, reference: Date = new Date()): Proximity {
  const days = daysUntil(event, reference);
  if (days == null) return 'later';
  if (days <= 7) return 'soon';
  if (days <= 30) return 'month';
  return 'later';
}

export function getMarkerOpacity(event: Partial<Event> | null | undefined, reference: Date = new Date()): number {
  return PROXIMITY_OPACITY[getProximity(event, reference)];
}

/**
 * SVG teardrop pin markup for a Leaflet `divIcon`. Highlighted pins get a ring
 * and full opacity so they stand out regardless of proximity.
 */
export function buildPinHtml(options: {
  color: string;
  opacity: number;
  isHighlighted?: boolean;
  count?: number;
}): string {
  const { color, opacity, isHighlighted = false, count = 1 } = options;
  const size = isHighlighted ? 40 : 32;
  const ring = isHighlighted
    ? `<circle cx="12" cy="12" r="11" fill="none" stroke="#262626" stroke-width="2" opacity="0.9" />`
    : '';
  const label = count > 1
    ? `<text x="12" y="16" text-anchor="middle" font-size="11" font-weight="700" fill="#ffffff">${count}</text>`
    : `<circle cx="12" cy="12" r="4" fill="#ffffff" />`;

  return `<svg width="${size}" height="${size * 1.25}" viewBox="0 0 24 30" xmlns="http://www.w3.org/2000/svg"
    style="opacity:${isHighlighted ? 1 : opacity};overflow:visible">
    <path d="M12 0C5.4 0 0 5.4 0 12c0 8.4 12 18 12 18s12-9.6 12-18c0-6.6-5.4-12-12-12z"
      fill="${color}" stroke="#ffffff" stroke-width="1.5" />
    ${label}
    ${ring}
  </svg>`;
}
